import React, { useState } from 'react';
import { useNavigate, useLocation, Link, Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext.jsx';
import { canAccess } from '../../config/roles.js';
import './login.scss';

const APP_NAME = 'TPA InsureOS';
const DEMO_PASSWORD = 'password123';

// Brand-panel value props (presentation only) — describe the platform.
const VALUE_PROPS = [
  {
    icon: 'fa fa-balance-scale',
    title: 'Unified claims adjudication',
    desc: 'Intake, review, and settle claims with statutory rules and AI-assisted decisions in one workspace.',
  },
  {
    icon: 'fa fa-notes-medical',
    title: 'Real-time eligibility & LOA',
    desc: 'Verify coverage and authorize outpatient, inpatient, dental, and veterinary care instantly.',
  },
  {
    icon: 'fa fa-shield-alt',
    title: 'Secure, auditable, compliant',
    desc: 'Role-based access, MFA policies, and full audit trails for regulatory and carrier oversight.',
  },
];

// Demo seed convention (backend/seeds/01_users.js): one account per business
// role at `<role-slug>@example.com`, password `password123`.
const slugEmail = (role) =>
  `${role
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '.')
    .replace(/^\.|\.$/g, '')}@example.com`;

const roleAccount = (role, context) => ({ title: role, context, email: slugEmail(role) });

// Demo accounts split by audience, mirroring the OS-series Customer / Staff toggle.
const DEMO_ACCOUNTS = {
  customer: [
    roleAccount('Member / Policy Holder', 'Member self-service portal'),
    roleAccount('Corporate Client', 'HR roster & enrollment'),
    roleAccount('Healthcare Provider', 'Provider portal & direct billing'),
    roleAccount('Dental Provider', 'Dental pre-authorization'),
    roleAccount('Veterinary Clinic', 'Pet pre-authorization'),
    roleAccount('Insurance Company Representative', 'Carrier program dashboard'),
  ],
  staff: [
    { title: 'Administrator', context: 'Superuser — full platform access', email: 'admin@example.com' },
    roleAccount('Operations Manager', 'Policy, workflow & AI config'),
    roleAccount('Claims Adjudicator', 'Adjudication workspace'),
    roleAccount('Claims Processor', 'Intake & OCR queue'),
    roleAccount('Medical Review Officer', 'Clinical pre-auth review'),
    roleAccount('Finance User', 'Disbursements & reconciliation'),
    roleAccount('Fraud Analyst', 'Fraud, waste & abuse detection'),
    roleAccount('Compliance Officer', 'Regulatory & audit reports'),
  ],
};

const AUDIENCES = [
  { key: 'customer', label: 'Customer' },
  { key: 'staff', label: 'Staff / Admin' },
];

/**
 * Shared authentication form for both sign-in and sign-up, presented in the
 * Xamun OS-series split-panel layout (brand/hero panel + form card) with the
 * signature Customer / Staff·Admin audience toggle and a one-click demo-account
 * quick-login list (dev only).
 *
 * Resolves the "no /login route" gap and the OS-series design-pattern concern:
 * anonymous users (and post-expiry redirects from ProtectedRoute / RoleRoute)
 * land on a real, on-brand page. Authenticates against POST /api/auth/login
 * (or /register) via the AuthContext, then returns the user to wherever they
 * were headed (location.state.from) or the landing route.
 */
function AuthForm({ mode }) {
  const isRegister = mode === 'register';
  const { user, login, register, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [audience, setAudience] = useState('customer');
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [demoLoading, setDemoLoading] = useState(null); // email being quick-logged-in
  const [showPassword, setShowPassword] = useState(false);

  // Already signed in → don't show the form; bounce to where they came from,
  // but only if this role can access it (else '/' → their role home). This
  // mirrors goToDestination so the post-login re-render — which hits this
  // branch once setUser fires — can't redirect a user into a 403.
  if (isAuthenticated) {
    return <Navigate to={safeDestination(user)} replace />;
  }

  function update(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
    setFieldErrors((e) => ({ ...e, [field]: undefined }));
  }

  function validate() {
    const errs = {};
    if (isRegister && !form.name.trim()) errs.name = 'Name is required.';
    if (!form.email.trim()) {
      errs.email = 'Email is required.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      errs.email = 'Enter a valid email address.';
    }
    if (!form.password) {
      errs.password = 'Password is required.';
    } else if (isRegister && form.password.length < 8) {
      errs.password = 'Password must be at least 8 characters.';
    }
    return errs;
  }

  // Resolve a safe post-login destination: the page the user was headed to
  // (location.state.from) only if their role can access it, else '/'
  // (LandingRedirect → their role's home). Prevents the common "log in → 403"
  // when the stored `from` belongs to another role's page (e.g. switching demo
  // accounts from a protected URL). Used by BOTH the post-login navigate and the
  // isAuthenticated re-render branch.
  function safeDestination(authedUser) {
    const from = location.state?.from?.pathname;
    if (from) {
      const slug = from.replace(/^\/+/, '').split('/')[0];
      if (canAccess(authedUser, slug)) return from;
    }
    return '/';
  }

  function goToDestination(authedUser) {
    navigate(safeDestination(authedUser), { replace: true });
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs);
      return;
    }

    setSubmitting(true);
    try {
      const authedUser = isRegister
        ? await register(form.email.trim(), form.name.trim(), form.password)
        : await login(form.email.trim(), form.password);
      goToDestination(authedUser);
    } catch (err) {
      setError(err?.message || 'Authentication failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  // One-click demo sign-in: fill the form for visibility, then authenticate.
  async function quickLogin(account) {
    setError('');
    setFieldErrors({});
    setForm({ name: '', email: account.email, password: DEMO_PASSWORD });
    setDemoLoading(account.email);
    try {
      const authedUser = await login(account.email, DEMO_PASSWORD);
      goToDestination(authedUser);
    } catch (err) {
      setError(err?.message || `Could not sign in as ${account.title}.`);
    } finally {
      setDemoLoading(null);
    }
  }

  const busy = submitting || demoLoading !== null;
  const accounts = DEMO_ACCOUNTS[audience];

  return (
    <div className="auth-split">
      {/* ---- Brand / hero panel ---- */}
      <aside className="auth-brand">
        <div className="auth-brand-decor" aria-hidden="true" />
        <div className="auth-brand-inner">
          <div className="d-flex align-items-center mb-4">
            <span className="auth-logo me-3">
              <i className="fa fa-shield-alt" aria-hidden="true" />
            </span>
            <span className="h5 mb-0 fw-bold">{APP_NAME}</span>
          </div>
          <h2 className="auth-headline">Third-Party Administration, end to end.</h2>
          <p className="auth-subhead">
            Claims, pre-authorization, finance, and compliance for medical, dental, and
            veterinary benefits — on one secure platform.
          </p>
          <div>
            {VALUE_PROPS.map((p) => (
              <div className="auth-prop" key={p.title}>
                <span className="auth-prop-icon">
                  <i className={p.icon} aria-hidden="true" />
                </span>
                <div>
                  <div className="auth-prop-title">{p.title}</div>
                  <p className="auth-prop-desc">{p.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </aside>

      {/* ---- Form panel ---- */}
      <main className="auth-form-side">
        <div className="auth-form-card">
          <div className="d-flex align-items-center mb-4 d-none auth-mobile-brand">
            <span className="auth-logo-on-light me-2">
              <i className="fa fa-shield-alt" aria-hidden="true" />
            </span>
            <span className="h6 mb-0 fw-bold">{APP_NAME}</span>
          </div>

          <h1 className="h3 mb-1 fw-bold">{isRegister ? 'Create your account' : 'Welcome back'}</h1>
          <p className="text-muted mb-4">
            {isRegister
              ? 'Sign up to get started.'
              : 'Sign in to the Third-Party Administration platform.'}
          </p>

          {/* Audience toggle (sign-in only) */}
          {!isRegister && (
            <div className="auth-tabs" role="tablist" aria-label="Account type">
              {AUDIENCES.map((a) => (
                <button
                  key={a.key}
                  type="button"
                  role="tab"
                  aria-selected={audience === a.key}
                  className={`auth-tab ${audience === a.key ? 'is-active' : ''}`}
                  onClick={() => setAudience(a.key)}
                  disabled={busy}
                >
                  {a.label}
                </button>
              ))}
            </div>
          )}

          {error && (
            <div className="alert alert-danger d-flex align-items-center" role="alert">
              <i className="fa fa-exclamation-circle me-2" aria-hidden="true" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate>
            {isRegister && (
              <div className="mb-3">
                <label htmlFor="name" className="form-label">
                  Full name
                </label>
                <input
                  id="name"
                  type="text"
                  className={`form-control ${fieldErrors.name ? 'is-invalid' : ''}`}
                  value={form.name}
                  onChange={(e) => update('name', e.target.value)}
                  autoComplete="name"
                  aria-invalid={!!fieldErrors.name}
                  disabled={busy}
                />
                {fieldErrors.name && <div className="invalid-feedback">{fieldErrors.name}</div>}
              </div>
            )}

            <div className="mb-3">
              <label htmlFor="email" className="form-label">
                Email
              </label>
              <input
                id="email"
                type="email"
                className={`form-control ${fieldErrors.email ? 'is-invalid' : ''}`}
                value={form.email}
                onChange={(e) => update('email', e.target.value)}
                placeholder="you@example.com"
                autoComplete="email"
                aria-invalid={!!fieldErrors.email}
                disabled={busy}
              />
              {fieldErrors.email && <div className="invalid-feedback">{fieldErrors.email}</div>}
            </div>

            <div className="mb-3">
              <div className="d-flex justify-content-between align-items-center">
                <label htmlFor="password" className="form-label mb-0">
                  Password
                </label>
                {!isRegister && (
                  <span className="auth-forgot text-muted small">Forgot password?</span>
                )}
              </div>
              <div className="input-group mt-1">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  className={`form-control ${fieldErrors.password ? 'is-invalid' : ''}`}
                  value={form.password}
                  onChange={(e) => update('password', e.target.value)}
                  autoComplete={isRegister ? 'new-password' : 'current-password'}
                  aria-invalid={!!fieldErrors.password}
                  disabled={busy}
                />
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setShowPassword((s) => !s)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  tabIndex={-1}
                >
                  <i className={`fa ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`} aria-hidden="true" />
                </button>
                {fieldErrors.password && <div className="invalid-feedback">{fieldErrors.password}</div>}
              </div>
            </div>

            <button type="submit" className="btn btn-primary w-100 auth-submit" disabled={busy}>
              {submitting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                  {isRegister ? 'Creating account…' : 'Signing in…'}
                </>
              ) : (
                <>{isRegister ? 'Create account' : 'Sign In'}</>
              )}
            </button>
          </form>

          <div className="text-center mt-4 small">
            {isRegister ? (
              <span>
                Already have an account?{' '}
                <Link to="/login" state={location.state}>
                  Sign in
                </Link>
              </span>
            ) : (
              <span className="text-muted">
                No account?{' '}
                <Link to="/register" state={location.state} className="fw-semibold">
                  Register here
                </Link>
              </span>
            )}
          </div>

          {!isRegister && (
            <div className="auth-demo">
              <div className="auth-demo-divider">
                <span>
                  <i className="fa fa-flask me-1" aria-hidden="true" />
                  Demo Accounts — Dev Only
                </span>
              </div>
              <div className="auth-demo-list">
                {accounts.map((acct) => {
                  const loading = demoLoading === acct.email;
                  return (
                    <button
                      key={acct.email}
                      type="button"
                      className="auth-demo-row"
                      onClick={() => quickLogin(acct)}
                      disabled={busy}
                    >
                      <span className="auth-demo-text">
                        <span className="auth-demo-name">{acct.title}</span>
                        <span className="auth-demo-role">{acct.context}</span>
                      </span>
                      {loading ? (
                        <span
                          className="spinner-border spinner-border-sm text-primary"
                          role="status"
                          aria-hidden="true"
                        />
                      ) : (
                        <i className="fa fa-arrow-right auth-demo-arrow" aria-hidden="true" />
                      )}
                    </button>
                  );
                })}
              </div>
              <p className="auth-demo-hint">All demo accounts use password <code>password123</code>.</p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

export function LoginPage() {
  return <AuthForm mode="login" />;
}

export function RegisterPage() {
  return <AuthForm mode="register" />;
}

export default LoginPage;
